import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import AdminShell from "./AdminShell";

const API = process.env.REACT_APP_API_URL || "";

const AdminContactMessages = () => {
  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;

  const [messages, setMessages] = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchMessages = useCallback(async (p = 1) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/contact?page=${p}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === 1) {
        setMessages(res.data.messages);
        setTotal(res.data.total);
        setPage(res.data.page);
        setPages(res.data.pages);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMessages(1); }, [fetchMessages]);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/api/admin/contact/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) => prev.map((m) => m._id === id ? { ...m, isRead: true } : m));
      if (selected?._id === id) setSelected((s) => ({ ...s, isRead: true }));
    } catch {}
  };

  const openMessage = (msg) => {
    setSelected(msg);
    if (!msg.isRead) markRead(msg._id);
  };

  const unreadCount = messages.filter((m) => !m.isRead).length;

  return (
    <AdminShell pageTitle="Contact Messages">
      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1.4fr" : "1fr", gap: 20, alignItems: "start" }}>

        {/* Message List */}
        <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "18px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Contact Messages</h3>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "#6b7280" }}>{total} total{unreadCount > 0 ? ` · ${unreadCount} unread` : ""}</p>
            </div>
            <button onClick={() => fetchMessages(page)} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
              Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>💬</div>
              No contact messages yet.
            </div>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    {["", "Name", "Email", "Subject", "Date"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr
                      key={msg._id}
                      onClick={() => openMessage(msg)}
                      style={{ cursor: "pointer", background: selected?._id === msg._id ? "#f0fdf4" : msg.isRead ? "#fff" : "#fffbeb", borderBottom: "1px solid #f3f4f6" }}
                    >
                      <td style={{ padding: "12px 14px", width: 8 }}>
                        {!msg.isRead && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#f59e0b" }} />}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: msg.isRead ? 400 : 600, color: "#111827" }}>{msg.name}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: "#6b7280" }}>{msg.email}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{msg.subject || "—"}</td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>{new Date(msg.createdAt).toLocaleDateString("en-SA", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pages > 1 && (
                <div style={{ padding: "12px 20px", borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Page {page} of {pages}</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button disabled={page <= 1} onClick={() => fetchMessages(page - 1)} style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid #e5e7eb", background: page <= 1 ? "#f9fafb" : "#fff", color: page <= 1 ? "#9ca3af" : "#374151", fontSize: 12, cursor: page <= 1 ? "default" : "pointer" }}>← Prev</button>
                    <button disabled={page >= pages} onClick={() => fetchMessages(page + 1)} style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid #e5e7eb", background: page >= pages ? "#f9fafb" : "#fff", color: page >= pages ? "#9ca3af" : "#374151", fontSize: 12, cursor: page >= pages ? "default" : "pointer" }}>Next →</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Message Detail Panel */}
        {selected && (
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", overflow: "hidden", position: "sticky", top: 20 }}>
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Message Detail</h3>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#9ca3af", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                {[
                  { label: "From", value: selected.name },
                  { label: "Email", value: <a href={`mailto:${selected.email}`} style={{ color: "#0e3725", textDecoration: "none" }}>{selected.email}</a> },
                  selected.phone && { label: "Phone", value: selected.phone },
                  selected.subject && { label: "Subject", value: selected.subject },
                  { label: "Received", value: new Date(selected.createdAt).toLocaleString("en-SA") },
                  { label: "Status", value: <span style={{ background: selected.isRead ? "#d1fae5" : "#fef3c7", color: selected.isRead ? "#065f46" : "#92400e", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{selected.isRead ? "Read" : "Unread"}</span> },
                ].filter(Boolean).map((item) => (
                  <div key={item.label}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#111827" }}>{item.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Message</div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>{selected.message}</p>
              </div>
              <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
                <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Your message to Treasury")}`} style={{ flex: 1, textAlign: "center", padding: "10px 16px", background: "#0e3725", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminContactMessages;
